import { fabric } from 'fabric';
import type { EditorPlugin, FabricEditor } from 'modules/fabric-editor';
import { FabricPluginName } from 'modules/fabric-editor';
import type { TemplateType } from '../template-type';
import {
  PlaceholderRenderContext,
  buildPreviewImageUrl,
  createImagePlaceholder,
  createImagePreviewGroup,
  createTextPlaceholder,
  getPlaceholderData,
  getScaledSize,
} from './placeholder-factory';
import type {
  AddImagePlaceholderOptions,
  AddTextPlaceholderOptions,
  PlaceholderData,
  PlaceholderFacade,
  PlaceholderImageData,
  PlaceholderKind,
  PlaceholderPluginOptions,
  PlaceholderTextData,
  ResolvedPlaceholderValue,
} from './types';

declare module 'modules/fabric-editor/domain/core/fabric-editor' {
  interface FabricEditor {
    placeholders?: PlaceholderFacade;
  }
}

const DEFAULT_TEXT: Omit<PlaceholderTextData, 'kind' | 'placeholderKey' | 'placeholderLabel' | 'sampleText'> = {
  fontFamily: 'Arial',
  fontWeight: 'normal',
  textAlign: 'center',
  fill: '#111827',
  minFontSize: 8,
  maxFontSize: 256,
};

const DEFAULT_IMAGE: Omit<PlaceholderImageData, 'kind' | 'placeholderKey' | 'placeholderLabel'> = {
  fitMode: 'contain',
};

export function placeholderPlugin(options: PlaceholderPluginOptions = {}): EditorPlugin {
  const defaults: typeof DEFAULT_TEXT = {
    fontFamily: options.defaultFontFamily ?? DEFAULT_TEXT.fontFamily,
    fontWeight: options.defaultFontWeight ?? DEFAULT_TEXT.fontWeight,
    textAlign: options.defaultTextAlign ?? DEFAULT_TEXT.textAlign,
    fill: options.defaultFill ?? DEFAULT_TEXT.fill,
    minFontSize: options.defaultMinFontSize ?? DEFAULT_TEXT.minFontSize,
    maxFontSize: options.defaultMaxFontSize ?? DEFAULT_TEXT.maxFontSize,
  };
  const imageDefaults: typeof DEFAULT_IMAGE = {
    fitMode: options.defaultFitMode ?? DEFAULT_IMAGE.fitMode,
  };

  return {
    name: FabricPluginName.PLACEHOLDER,
    install(editor: FabricEditor) {
      const canvas = editor.canvas;
      const imageCache = new Map<string, fabric.Image>();
      const tokenByGroup = new WeakMap<fabric.Group, string>();
      let currentTemplateType: TemplateType | undefined;
      const ctx = (): PlaceholderRenderContext => ({ templateType: currentTemplateType });

      editor.registerObjectType<PlaceholderImageData>({
        kind: 'placeholder-image',
        create: (props) => createImagePlaceholder(props, { width: 320, height: 240, ...props }, ctx()),
      });
      editor.registerObjectType<PlaceholderTextData>({
        kind: 'placeholder-text',
        create: (props) => createTextPlaceholder(props, { width: 400, height: 100, ...props }, ctx()),
      });

      // Why: a swap is a purely visual re-derivation of an existing placeholder
      // (refit, preview load, resolved image). It removes + re-inserts a group,
      // which fires object:removed/object:added. Without suspending history,
      // each swap is recorded as a user undo step — and the remove-then-insert
      // ordering produces an intermediate snapshot where the placeholder is
      // momentarily absent, so a later Ctrl+Z can delete an untouched
      // placeholder. Running the swap inside history.transaction makes it
      // invisible to undo/redo; the real edit (property change, resize) is
      // already recorded via the object:modified that triggered the swap.
      const swap = (oldGroup: fabric.Group, fresh: fabric.Group): void => {
        const run = () => swapImpl(oldGroup, fresh);
        if (editor.history) editor.history.transaction(run);
        else run();
      };

      const swapImpl = (oldGroup: fabric.Group, fresh: fabric.Group): void => {
        const idx = canvas.getObjects().indexOf(oldGroup);
        // Why: if oldGroup is no longer on the canvas, a previous swap already
        // replaced it. Adding `fresh` here would duplicate the placeholder
        // (seen when a stale refit fires after a draft-commit cycle).
        if (idx < 0) return;
        const active = canvas.getActiveObject();
        const wasSingleActive = active === oldGroup;
        // Why: when the user has marquee-selected multiple placeholders,
        // Fabric wraps them in an ActiveSelection. We must update that
        // selection's membership across the swap so the user's selection
        // survives (otherwise refit silently deselects everything mid-edit).
        const activeSelection =
          active && active.type === 'activeSelection'
            ? (active as fabric.ActiveSelection)
            : null;
        const memberOfActive = !!activeSelection && activeSelection.contains(oldGroup);
        if (memberOfActive && activeSelection) {
          activeSelection.removeWithUpdate(oldGroup);
        }
        canvas.remove(oldGroup);
        canvas.insertAt(fresh, idx, false);
        if (memberOfActive && activeSelection) {
          activeSelection.addWithUpdate(fresh);
          // Why: Fabric's addWithUpdate / removeWithUpdate mutate the
          // ActiveSelection's `_objects` array but do not fire any canvas
          // selection event. Without this nudge, consumers holding the
          // previous member array (e.g. React state in the right toolbar)
          // keep stale references to the now-removed `oldGroup`, and the
          // next refit no-ops via the `idx < 0` stale-guard at the top of
          // this function — i.e. multi-select edits would apply once and
          // then silently stop working.
          editor.emit('selection:changed', { active: canvas.getActiveObjects() });
        } else if (wasSingleActive) {
          canvas.setActiveObject(fresh);
        }
        canvas.requestRenderAll();
      };

      const loadPreviewImage = (url: string): Promise<fabric.Image | null> => {
        const cached = imageCache.get(url);
        if (cached) return Promise.resolve(cached);
        return new Promise((resolve) => {
          fabric.Image.fromURL(url, (img) => {
            const element = img && (img.getElement?.() as HTMLImageElement | null);
            const ok = element && element.naturalWidth > 0 && element.naturalHeight > 0;
            if (!ok) {
              // eslint-disable-next-line no-console
              console.warn('[placeholderPlugin] preview image failed to load:', url);
              resolve(null);
              return;
            }
            imageCache.set(url, img);
            resolve(img);
          }, { crossOrigin: 'anonymous' });
        });
      };

      const replaceGroup = (group: fabric.Group): fabric.Group | null => {
        const data = getPlaceholderData(group);
        if (!data) return null;
        const { width, height } = getScaledSize(group);
        // Why: when `group` is a member of a Fabric ActiveSelection (the
        // synthetic group Fabric creates around a marquee multi-selection),
        // its `left`/`top` are stored *relative to the selection's center*,
        // not as canvas-absolute coordinates. Reading them directly would
        // place the fresh group near the canvas origin.
        //
        // `calcTransformMatrix()` returns the absolute canvas-relative
        // transform matrix regardless of parent nesting; matrix[4] and
        // matrix[5] are the object's absolute center on canvas. We then
        // convert center → top-left because the placeholder group uses
        // originX='left', originY='top' (see COMMON_GROUP_PROPS in
        // placeholder-factory.ts).
        //
        // Reference: https://davelage.com/posts/fabric-selection-group-positioning/
        // ("calcTransformMatrix() is the true transforms in relation to the
        // canvas, while calcOwnMatrix() is the transforms in relation to
        // the parent (canvas or group).")
        let left = group.left;
        let top = group.top;
        if (group.group && group.group.type === 'activeSelection') {
          const m = group.calcTransformMatrix();
          left = m[4] - width / 2;
          top = m[5] - height / 2;
        }
        const geometry = { left, top, angle: group.angle, width, height };

        if (data.kind === 'placeholder-image' && data.previewMode) {
          const loading = createImagePlaceholder(data, geometry, ctx());
          swap(group, loading);
          const url = buildPreviewImageUrl(data, width, height);
          tokenByGroup.set(loading, url);
          loadPreviewImage(url).then((img) => {
            if (!img) return;
            if (tokenByGroup.get(loading) !== url) return;
            if (canvas.getObjects().indexOf(loading) < 0) return;
            const clone = (img as unknown as { __cloned?: boolean }).__cloned ? img : cloneImageInstance(img);
            const ready = createImagePreviewGroup(data, geometry, clone);
            swap(loading, ready);
          });
          return loading;
        }

        const fresh =
          data.kind === 'placeholder-image'
            ? createImagePlaceholder(data, geometry, ctx())
            : createTextPlaceholder(data, geometry, ctx());
        swap(group, fresh);
        return fresh;
      };

      const refitAll = (): void => {
        // Snapshot the list — replaceGroup mutates the canvas as it walks.
        const targets = canvas.getObjects().filter((o): o is fabric.Group => !!getPlaceholderData(o));
        for (const t of targets) replaceGroup(t);
      };

      // Why: when the user marquee-selects multiple placeholders, Fabric wraps
      // them in an ActiveSelection and applies a resize to *that* synthetic
      // group. object:modified then fires with `target` = the ActiveSelection,
      // which carries no placeholder data — so the single-object handler below
      // early-returns and nothing is refit. The members keep the selection's
      // scaleX/scaleY, so a text placeholder ends up visually squished instead
      // of re-fitting its font to the new box.
      //
      // discardActiveObject() makes Fabric *bake* the selection's transform
      // (scale, angle, position) into every child via ActiveSelection.onDeselect
      // → destroy() → _restoreObjectsState(). After that each member is a
      // top-level canvas object carrying the correct absolute scale/coords, so
      // replaceGroup() handles it exactly like a single-object resize — no
      // manual matrix decomposition needed. We then re-create the selection over
      // the fresh groups so the user's marquee survives the refit.
      const refitSelection = (sel: fabric.ActiveSelection): void => {
        const ssx = sel.scaleX ?? 1;
        const ssy = sel.scaleY ?? 1;
        // Pure move (no scaling) — members keep scale 1, nothing to refit. Leave
        // the selection untouched so a drag doesn't drop the user's marquee.
        if (Math.abs(ssx - 1) < 1e-6 && Math.abs(ssy - 1) < 1e-6) return;

        // Snapshot ALL members in order before dismantling the selection: the
        // selection can mix placeholders with plain rects/images, and we must
        // rebuild it over the full set — only the placeholders are re-derived,
        // every other object stays selected as-is. (Filtering to placeholders
        // here would silently drop the non-placeholder members from the user's
        // selection after the resize.)
        const members = sel.getObjects().slice();
        const placeholders = members.filter((o): o is fabric.Group => !!getPlaceholderData(o));
        if (placeholders.length === 0) return;

        const run = () => {
          // Bakes the selection transform into each member (see comment above).
          canvas.discardActiveObject();
          const replacement = new Map<fabric.Object, fabric.Group>();
          for (const m of placeholders) {
            const f = replaceGroup(m);
            if (f) replacement.set(m, f);
          }
          // Rebuild the user's selection in its original order: refit
          // placeholders take their fresh group; non-placeholder members and
          // any placeholder that didn't get replaced stay unchanged.
          const next = members.map((m) => replacement.get(m) ?? m);
          if (next.length > 1) {
            const nextSel = new fabric.ActiveSelection(next, { canvas });
            canvas.setActiveObject(nextSel);
          } else if (next.length === 1) {
            canvas.setActiveObject(next[0]);
          }
          canvas.requestRenderAll();
          // Why: nudge consumers (right toolbar / inspector) so they drop stale
          // references to the now-removed members — same reasoning as swap().
          editor.emit('selection:changed', { active: canvas.getActiveObjects() });
        };

        // Keep discard + N swaps + re-select atomic in history (transaction is
        // re-entrant, so the per-swap transactions nest harmlessly).
        if (editor.history) editor.history.transaction(run);
        else run();
      };

      const onModified = (payload: { target: fabric.Object }) => {
        const target = payload.target as fabric.Object | undefined;
        if (!target) return;
        if (target.type === 'activeSelection') {
          refitSelection(target as fabric.ActiveSelection);
          return;
        }
        const group = target as fabric.Group;
        const data = getPlaceholderData(group);
        if (!data) return;
        const sx = group.scaleX ?? 1;
        const sy = group.scaleY ?? 1;
        if (Math.abs(sx - 1) < 1e-6 && Math.abs(sy - 1) < 1e-6) return;
        replaceGroup(group);
      };

      const offModified = editor.on('object:modified', onModified);
      // Why: when the canvas is replaced wholesale (template load, undo/redo,
      // render-page restore) existing placeholder groups need their visuals
      // re-derived against the current template type — otherwise invalid
      // placeholders won't switch to the red warning style until the user
      // re-saves them.
      const offLoaded = editor.on('canvas:loaded', () => {
        refitAll();
      });
      // Why: a web font finishing loading changes text metrics. Placeholders
      // that were first fitted against the fallback (Arial) — because the real
      // font hadn't arrived yet — must re-fit so both their font and their
      // fitted size are correct (the font-loading plugin has already repainted
      // the raw glyphs, but not re-run the box-fitting this owns). A background
      // font load is not a user edit, so the template's dirty flag is preserved
      // across the refit — otherwise merely opening a template would show
      // "unsaved changes" the moment its fonts finish loading.
      const offFonts = editor.on('fonts:loaded', () => {
        const wasDirty = editor.dirty?.isDirty() ?? false;
        refitAll();
        if (!wasDirty) editor.dirty?.markClean();
      });

      const facade: PlaceholderFacade & { __off?: () => void } = {
        addImage(opts) {
          const data: PlaceholderImageData = {
            kind: 'placeholder-image',
            placeholderKey: opts.placeholderKey ?? '',
            placeholderLabel: opts.placeholderLabel ?? '',
            teamNumber: opts.teamNumber,
            playerNumber: opts.playerNumber,
            fitMode: opts.fitMode ?? imageDefaults.fitMode,
          };
          const fallback = editor.getViewportTopLeft();
          const group = createImagePlaceholder(data, {
            left: opts.left ?? fallback.left,
            top: opts.top ?? fallback.top,
            angle: opts.angle,
            width: opts.width,
            height: opts.height,
          }, ctx());
          canvas.add(group);
          canvas.setActiveObject(group);
          canvas.requestRenderAll();
          return group;
        },
        addText(opts) {
          const data: PlaceholderTextData = {
            kind: 'placeholder-text',
            placeholderKey: opts.placeholderKey ?? '',
            placeholderLabel: opts.placeholderLabel ?? '',
            teamNumber: opts.teamNumber,
            playerNumber: opts.playerNumber,
            sampleText: opts.sampleText ?? 'Sample text',
            fontFamily: opts.fontFamily ?? defaults.fontFamily,
            fontWeight: opts.fontWeight ?? defaults.fontWeight,
            fontStyle: opts.fontStyle,
            textAlign: opts.textAlign ?? defaults.textAlign,
            fill: opts.fill ?? defaults.fill,
            minFontSize: opts.minFontSize ?? defaults.minFontSize,
            maxFontSize: opts.maxFontSize ?? defaults.maxFontSize,
          };
          const fallback = editor.getViewportTopLeft();
          const group = createTextPlaceholder(data, {
            left: opts.left ?? fallback.left,
            top: opts.top ?? fallback.top,
            angle: opts.angle,
            width: opts.width,
            height: opts.height,
          }, ctx());
          canvas.add(group);
          canvas.setActiveObject(group);
          canvas.requestRenderAll();
          return group;
        },
        refit(target) {
          replaceGroup(target);
        },
        refitAll() {
          refitAll();
        },
        setTemplateType(type) {
          if (currentTemplateType === type) return;
          currentTemplateType = type;
          refitAll();
        },
        getTemplateType() {
          return currentTemplateType;
        },
        isPlaceholder(target): target is fabric.Group {
          return getPlaceholderData(target ?? undefined) !== undefined;
        },
        getKind(target): PlaceholderKind | undefined {
          return getPlaceholderData(target ?? undefined)?.kind;
        },
        applyResolved(target, value: ResolvedPlaceholderValue) {
          const data = getPlaceholderData(target);
          if (!data) return Promise.resolve(true);
          const { width, height } = getScaledSize(target);
          const geometry = { left: target.left, top: target.top, angle: target.angle, width, height };

          if (data.kind === 'placeholder-text' && value.type === 'text') {
            const next: PlaceholderTextData = { ...data, resolvedText: value.value, previewMode: false };
            const fresh = createTextPlaceholder(next, geometry);
            swap(target, fresh);
            return Promise.resolve(true);
          }

          if (data.kind === 'placeholder-image' && value.type === 'image') {
            const next: PlaceholderImageData = { ...data, resolvedImageUrl: value.url, previewMode: false };
            return new Promise<boolean>((resolve) => {
              fabric.Image.fromURL(value.url, (img) => {
                const element = img && (img.getElement?.() as HTMLImageElement | null);
                const ok = element && element.naturalWidth > 0 && element.naturalHeight > 0;
                if (!ok) {
                  // eslint-disable-next-line no-console
                  console.warn('[placeholderPlugin] resolved image failed to load:', value.url);
                  resolve(false);
                  return;
                }
                if (canvas.getObjects().indexOf(target) < 0) {
                  resolve(true);
                  return;
                }
                const ready = createImagePreviewGroup(next, geometry, img);
                swap(target, ready);
                resolve(true);
              }, { crossOrigin: 'anonymous' });
            });
          }

          return Promise.resolve(true);
        },
      };

      facade.__off = () => {
        offModified();
        offLoaded();
        offFonts();
      };
      editor.placeholders = facade;
    },
    dispose(editor: FabricEditor) {
      const facade = editor.placeholders as (PlaceholderFacade & { __off?: () => void }) | undefined;
      facade?.__off?.();
      editor.placeholders = undefined;
    },
  };
}

export function isPlaceholderData(data: unknown): data is PlaceholderData {
  if (!data || typeof data !== 'object') return false;
  const kind = (data as { kind?: unknown }).kind;
  return kind === 'placeholder-image' || kind === 'placeholder-text';
}

function cloneImageInstance(img: fabric.Image): fabric.Image {
  // Why: a fabric.Image can only live in one group at a time. Wrap the same
  // underlying HTMLImageElement in a new fabric.Image so we can place it
  // freely across refits without ripping it out of a previous group.
  const element = img.getElement() as HTMLImageElement;
  const clone = new fabric.Image(element);
  (clone as unknown as { __cloned?: boolean }).__cloned = true;
  return clone;
}
